/**
 * Core Runtime - Deterministic application lifecycle coordinator.
 *
 * Owns the boot sequence, module registration, and shutdown.
 * Every subsystem registers as a module with explicit init/teardown.
 * Nothing runs outside this runtime.
 */

import { logger }              from "@/system/logger";
import { eventBus }             from "./eventBus";
import { initMigrationEngine }  from "@/persistence/migration/index";

export type ModuleStatus = "registered" | "initializing" | "ready" | "error" | "stopped";

/**
 * Module health status level.
 *
 * GOVERNANCE RULE (Canonical Ownership Doctrine §XII + Subsystem 6):
 *   "Unknown is acceptable. Fabricated certainty is forbidden."
 *
 *   "healthy"   - Module ACTIVELY verified its own operational state.
 *   "degraded"  - Module operates with reduced capability OR an upstream dependency is unhealthy.
 *   "unhealthy" - Module is non-operational.
 *   "unknown"   - Health cannot be determined without I/O, or healthCheck() not implemented.
 *                 This is the truthful default - not an error state.
 */
export type ModuleHealthStatusLevel =
  | "healthy"
  | "degraded"
  | "unhealthy"
  | "unknown";

/**
 * Snapshot of a module's health at a point in time.
 * Produced by healthCheck() and by cascade propagation in ModuleHealthMonitor.
 */
export interface ModuleHealthStatus {
  /** Truthful status - never fabricated. */
  status:        ModuleHealthStatusLevel;
  /** Human-readable detail - what was checked or why status is what it is. */
  detail?:       string;
  /** Epoch ms when this check was performed. */
  checkedAt:     number;
  /**
   * If this status was caused by a cascade from an upstream module,
   * this field carries that module's ID.
   */
  cascadedFrom?: string;
}

export interface RuntimeModule {
  id: string;
  name: string;
  /** Called during Runtime.start(). Must be idempotent. */
  init: () => void | Promise<void>;
  /** Called during Runtime.shutdown(). Cleanup resources. */
  teardown?: () => void;
  /** Optional dependency IDs - these modules init first. */
  dependencies?: string[];
  /**
   * Optional periodic health check. Called by ModuleHealthMonitor every ~30 seconds.
   *
   * CONTRACT (Canonical Ownership Doctrine §XII):
   *   - MUST be synchronous - no network calls, no I/O.
   *   - MUST NOT mutate any state.
   *   - MUST return within 50ms.
   *   - Return "unknown" if health cannot be determined without I/O.
   *   - Return "healthy" ONLY if the module ACTIVELY verified its own state.
   *   - Assumption of health ("probably fine") must return "unknown", not "healthy".
   *
   * Modules without healthCheck() report "unknown" automatically.
   * "unknown" is truthful and acceptable. "healthy" without verification is forbidden.
   */
  healthCheck?: () => ModuleHealthStatus;
}

interface ModuleEntry {
  module:        RuntimeModule;
  status:        ModuleStatus;
  initDuration?: number;
  error?:        string;
  /** Populated by ModuleHealthMonitor after first health assessment. */
  health?:       ModuleHealthStatus;
}

export type RuntimePhase = "idle" | "booting" | "running" | "shutting_down" | "stopped";

class NuvraRuntime {
  private modules = new Map<string, ModuleEntry>();
  private phase: RuntimePhase = "idle";
  private bootTimestamp = 0;

  /** Current runtime phase. */
  getPhase(): RuntimePhase {
    return this.phase;
  }

  /** Register a module. Must be called before start(). */
  registerModule(module: RuntimeModule): void {
    if (this.modules.has(module.id)) {
      logger.warn(`Module "${module.id}" already registered - skipping`);
      return;
    }
    this.modules.set(module.id, { module, status: "registered" });
    eventBus.emit({ type: "module:registered", moduleId: module.id });
  }

  /** Boot all registered modules in dependency order. */
  async start(): Promise<void> {
    if (this.phase !== "idle" && this.phase !== "stopped") {
      logger.warn(`Runtime.start() called in "${this.phase}" phase - ignoring`);
      return;
    }

    this.phase = "booting";
    this.bootTimestamp = Date.now();

    // Migration engine runs FIRST - before any persistence reads occur.
    // This handles: crash recovery, ledger versioning, prune of old history.
    // All subsequent module init() calls will load already-migrated data.
    try {
      initMigrationEngine();
    } catch (err) {
      logger.error("Migration engine init failed - continuing boot", { error: err });
    }

    eventBus.emit({ type: "runtime:init" });
    logger.info("Runtime starting", { moduleCount: this.modules.size });

    const order = this.resolveInitOrder();

    for (const id of order) {
      const entry = this.modules.get(id);
      if (!entry) continue;

      entry.status = "initializing";
      const start = performance.now();

      try {
        await entry.module.init();
        entry.status = "ready";
        entry.initDuration = Math.round(performance.now() - start);
        eventBus.emit({ type: "module:initialized", moduleId: id });
        logger.debug(`Module "${entry.module.name}" ready (${entry.initDuration}ms)`);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        entry.status = "error";
        entry.error = message;
        eventBus.emit({ type: "module:error", moduleId: id, error: message });
        logger.error(`Module "${entry.module.name}" failed to init`, { error: message });
        // Non-fatal: continue booting other modules
      }
    }

    this.phase = "running";
    eventBus.emit({ type: "runtime:ready" });
    const bootDuration = Date.now() - this.bootTimestamp;
    logger.info(`Runtime ready in ${bootDuration}ms`, {
      modules: order.length,
      failed: [...this.modules.values()].filter((e) => e.status === "error").length,
    });
  }

  /** Gracefully tear down all modules in reverse order. */
  shutdown(): void {
    if (this.phase !== "running") return;

    this.phase = "shutting_down";
    eventBus.emit({ type: "runtime:shutdown" });
    logger.info("Runtime shutting down");

    const order = this.resolveInitOrder().reverse();
    for (const id of order) {
      const entry = this.modules.get(id);
      if (!entry || entry.status === "error") continue;
      try {
        entry.module.teardown?.();
        entry.status = "stopped";
      } catch (err) {
        logger.error(`Module "${entry.module.name}" teardown failed`, { error: err });
      }
    }

    eventBus.clear();
    this.phase = "stopped";
    logger.info("Runtime stopped");
  }

  /** Get status of all modules, including health if assessed. */
  getModuleStatuses(): Array<{
    id:           string;
    name:         string;
    status:       ModuleStatus;
    initDuration?: number;
    error?:       string;
    health:       ModuleHealthStatus | null;
  }> {
    return [...this.modules.values()].map((e) => ({
      id:           e.module.id,
      name:         e.module.name,
      status:       e.status,
      initDuration: e.initDuration,
      error:        e.error,
      health:       e.health ?? null,
    }));
  }

  /** Check if a specific module is ready. */
  isModuleReady(moduleId: string): boolean {
    return this.modules.get(moduleId)?.status === "ready";
  }

  /**
   * Expose the module registry to ModuleHealthMonitor (read-only projection).
   * Does NOT expose the mutable ModuleEntry - only what the health monitor needs.
   *
   * Called by ModuleHealthMonitor to build the dependency graph and iterate modules.
   */
  getRegisteredModules(): ReadonlyArray<{
    id:            string;
    name:          string;
    status:        ModuleStatus;
    dependencies:  string[];
    hasHealthCheck: boolean;
  }> {
    return [...this.modules.values()].map(e => ({
      id:             e.module.id,
      name:           e.module.name,
      status:         e.status,
      dependencies:   e.module.dependencies ?? [],
      hasHealthCheck: typeof e.module.healthCheck === "function",
    }));
  }

  /**
   * Invoke a module's healthCheck() safely.
   * Returns null if the module has no healthCheck().
   * Returns { status: "unhealthy" } if healthCheck() throws.
   *
   * Called by ModuleHealthMonitor - never call directly from application code.
   */
  checkModuleHealth(moduleId: string): ModuleHealthStatus | null {
    const entry = this.modules.get(moduleId);
    if (!entry?.module.healthCheck) return null;
    try {
      return entry.module.healthCheck();
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      logger.warn(`healthCheck() threw for module "${moduleId}"`, { error: detail });
      return {
        status:    "unhealthy",
        detail:    `healthCheck() threw: ${detail}`,
        checkedAt: Date.now(),
      };
    }
  }

  /**
   * Store a health assessment result for a module.
   * Called by ModuleHealthMonitor after each assessment cycle.
   *
   * Write authority: ModuleHealthMonitor only (Canonical Ownership Doctrine §Appendix A).
   */
  setModuleHealth(moduleId: string, health: ModuleHealthStatus): void {
    const entry = this.modules.get(moduleId);
    if (entry) entry.health = health;
  }

  /** Topological sort based on dependencies. */
  private resolveInitOrder(): string[] {
    const visited = new Set<string>();
    const order: string[] = [];

    const visit = (id: string) => {
      if (visited.has(id)) return;
      visited.add(id);
      const entry = this.modules.get(id);
      if (!entry) return;
      for (const dep of entry.module.dependencies || []) {
        if (this.modules.has(dep)) visit(dep);
      }
      order.push(id);
    };

    for (const id of this.modules.keys()) visit(id);
    return order;
  }

  /** Uptime in milliseconds. */
  getUptime(): number {
    if (this.phase !== "running") return 0;
    return Date.now() - this.bootTimestamp;
  }
}

export const nuvraRuntime = new NuvraRuntime();
